import React, { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { CheckCircle2, Calendar, Clock, Phone, Mail, ArrowLeft } from 'lucide-react';
import { brand } from '../mock';

const Success = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const registrationData = location.state;

  const batchTimes = { morning: '6:00 AM', day: '11:00 AM', evening: '6:00 PM' };

  useEffect(() => {
    if (!registrationData) navigate('/');
  }, [registrationData, navigate]);

  // Fire the Energy Analysis registration conversion once, after a successful
  // submission lands on /success. (Base page_view/remarketing already handled by the on-page gtag config.)
  const conversionFired = useRef(false);
  useEffect(() => {
    if (
      registrationData &&
      !conversionFired.current &&
      typeof window !== 'undefined' &&
      typeof window.gtag === 'function'
    ) {
      conversionFired.current = true;
      window.gtag('event', 'conversion', {
        send_to: 'AW-17349611434/wu6ECMfG-NQcEKqf-NBA',
        value: 1.0,
        currency: 'INR',
      });
    }
  }, [registrationData]);

  if (!registrationData) return null;

  const phones = ['7416852211', '7416879900', '6301066498'];

  const details = [
    { label: 'Name', value: registrationData.name },
    { label: 'Age', value: `${registrationData.age} years` },
    { label: 'Phone', value: registrationData.phone },
    { label: 'Batch', value: `${registrationData.batch.charAt(0).toUpperCase() + registrationData.batch.slice(1)} - ${batchTimes[registrationData.batch]}` }
  ];

  return (
    <div className="min-h-screen bg-[#2A0A18] text-[#F8F6F0]" data-testid="success-page">
      <header className="border-b border-[#D4AF37]/10 backdrop-blur-2xl bg-[#2A0A18]/70">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center gap-3">
          <img src={brand.logo} alt="logo" className="w-11 h-11 rounded-full" />
          <span className="font-heading text-xl gold-text">Bhagavadgita Healing</span>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-20">
        <div className="text-center mb-14">
          <div className="w-20 h-20 border border-[#D4AF37]/40 rounded-full flex items-center justify-center mx-auto mb-6 animate-gold-pulse">
            <CheckCircle2 className="w-10 h-10 text-[#D4AF37]" />
          </div>
          <p className="text-xs tracking-[0.3em] uppercase text-[#D4AF37] mb-4">Registration Successful</p>
          <h1 className="font-heading text-4xl md:text-5xl font-light gold-text mb-4">Welcome, {registrationData.name}</h1>
          <p className="text-[#E6CFC2] text-lg">Your seat for the Bhagavadgita Healing Programme is reserved.</p>
        </div>

        {/* Details */}
        <div className="bg-[#3A1226] border border-[#D4AF37]/20 p-8 mb-8">
          <h2 className="text-xs tracking-[0.3em] uppercase text-[#D4AF37] mb-6">Your Registration</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            {details.map((d) => (
              <div key={d.label}>
                <p className="text-[11px] tracking-widest uppercase text-[#B89A8B] mb-1">{d.label}</p>
                <p className="text-lg text-[#F8F6F0]">{d.value}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Programme info */}
        <div className="bg-[#3A1226] border border-[#D4AF37]/20 p-8 mb-8">
          <h2 className="text-xs tracking-[0.3em] uppercase text-[#D4AF37] mb-6">Programme Details</h2>
          <div className="space-y-4">
            <p className="flex items-center gap-3 text-[#E6CFC2]"><Calendar className="w-5 h-5 text-[#D4AF37]" /> Start Date: <span className="text-[#F8F6F0]">July 13, 2026</span></p>
            <p className="flex items-center gap-3 text-[#E6CFC2]"><Clock className="w-5 h-5 text-[#D4AF37]" /> Duration: <span className="text-[#F8F6F0]">5 Days (Monday to Friday)</span></p>
            <p className="flex items-center gap-3 text-[#E6CFC2]"><Clock className="w-5 h-5 text-[#D4AF37]" /> Your Batch: <span className="text-[#F8F6F0]">{batchTimes[registrationData.batch]}</span></p>
          </div>
          <div className="border-l-2 border-[#D4AF37]/40 pl-5 mt-6">
            <p className="text-[#F8F6F0] font-medium mb-1">మీరు విజయవంతంగా రిజిస్టర్ అయ్యారు.</p>
            <p className="text-[#E6CFC2] text-sm leading-relaxed">
              గురుజీ వ్యక్తిగతంగా మీకు కాల్ చేసి, తదుపరి సూచనలు మరియు సెషన్ వివరాలు తెలియజేస్తారు. దయచేసి మీ ఫోన్ దగ్గరలో ఉంచుకోండి.
            </p>
          </div>
        </div>

        <div className="text-center">
          <Button
            onClick={() => navigate('/')}
            variant="outline"
            className="bg-transparent text-[#D4AF37] border border-[#D4AF37]/40 hover:border-[#D4AF37] hover:bg-transparent px-8 py-6 rounded-none uppercase tracking-widest text-sm"
            data-testid="back-home-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
          </Button>
        </div>

        <div className="mt-14 text-center border-t border-[#D4AF37]/10 pt-10">
          <p className="text-xs tracking-[0.3em] uppercase text-[#D4AF37] mb-4">Need Help?</p>
          <div className="flex flex-wrap gap-4 justify-center items-center">
            {phones.map((p) => (
              <a key={p} href={`tel:${p}`} className="flex items-center gap-2 text-[#E6CFC2] hover:text-[#D4AF37] transition-colors">
                <Phone className="w-4 h-4" /> +91 {p}
              </a>
            ))}
          </div>
          <a href="mailto:spirituallifeskills18@gmail.com" className="inline-flex items-center gap-2 text-[#E6CFC2] hover:text-[#D4AF37] transition-colors mt-3">
            <Mail className="w-4 h-4" /> spirituallifeskills18@gmail.com
          </a>
        </div>
      </div>
    </div>
  );
};

export default Success;
